import { useState } from 'react';

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  /** Branches observed on past PRs, offered as one-click chips. */
  suggestions?: string[];
}

/**
 * Editor for a list of branch patterns. Supports exact names (`main`), prefix globs
 * (`release/*`), and the `*` wildcard (all branches). Add via the button or Enter — both use
 * a plain click handler, so the old "mouse-click drops the first value" bug is gone.
 */
export default function BranchListEditor({ value, onChange, suggestions }: Props) {
  const [input, setInput] = useState('');

  const add = (raw: string) => {
    const v = raw.trim();
    setInput('');
    if (!v || value.includes(v)) return;
    onChange([...value, v]);
  };

  const remove = (v: string) => onChange(value.filter(x => x !== v));

  const hasAll = value.includes('*');
  const recent = (suggestions ?? []).filter(s => !value.includes(s)).slice(0, 8);

  return (
    <div className="branch-editor">
      <div className="branch-chips">
        {value.length === 0 ? (
          <span className="muted small">No branches — nothing will be analyzed.</span>
        ) : (
          value.map(b => (
            <span key={b} className="multiselect-chip">
              {b === '*' ? '∗ all branches' : b}
              <button
                type="button"
                className="multiselect-chip-remove"
                aria-label={`Remove ${b}`}
                onClick={() => remove(b)}
              >
                ×
              </button>
            </span>
          ))
        )}
      </div>

      <div className="branch-add">
        <input
          className="input"
          value={input}
          placeholder="e.g. main or release/*"
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add(input);
            }
          }}
        />
        <button type="button" className="btn" onClick={() => add(input)} disabled={!input.trim()}>
          Add
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => add('*')}
          disabled={hasAll}
          title="Analyze every branch"
        >
          All branches
        </button>
      </div>

      <p className="muted small" style={{ margin: '0.4rem 0 0' }}>
        <code>*</code> = all branches · <code>release/*</code> = any branch under <code>release/</code> · or an exact name.
      </p>

      {recent.length > 0 ? (
        <div className="branch-suggest">
          <span className="muted small">Recent:</span>
          {recent.map(s => (
            <button key={s} type="button" className="branch-suggest-chip" onClick={() => add(s)}>
              {s}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
